import { useMemo, memo, useCallback } from "react";
import { List } from "react-window";
import { Search, GripVertical, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CATEGORIES, CATEGORY_COLORS } from "@/lib/constants";
import { getThumbnail } from "@/lib/video";

/**
 * Virtualized technique picker used by the sequence builder sidebar (desktop
 * expanded) and the mobile bottom sheet. Search + filter chips are pinned
 * above a react-window list so the panel stays performant with thousands of
 * techniques.
 */
const DESKTOP_ROW_HEIGHT = 60;
const MOBILE_ROW_HEIGHT = 72;

const PaletteRow = memo(function PaletteRow({ index, style, techniques, variant, onDragStart, onClick }) {
  const t = techniques[index];
  const isMobile = variant === "mobile";
  const testid = isMobile ? `mobile-palette-item-${t.id}` : `palette-item-${t.id}`;
  // Reserve consistent inner spacing that matches the row size.
  const inner = (
    <div className={`group flex items-center gap-2 h-full ${isMobile ? "p-2.5" : "p-2"} rounded-lg bg-card border border-border ${isMobile ? "active:border-primary active:bg-primary/5" : "hover:border-primary/60 cursor-grab active:cursor-grabbing"} transition-colors`}>
      {!isMobile && <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />}
      <div className={`relative ${isMobile ? "h-11 w-14" : "h-9 w-12"} rounded bg-black overflow-hidden shrink-0`}>
        <img src={getThumbnail(t)} alt="" className={`absolute inset-0 w-full h-full object-cover ${isMobile ? "opacity-60" : "opacity-50"}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`${isMobile ? "text-sm" : "text-xs"} font-medium leading-tight line-clamp-2 break-words`}>{t.name}</p>
        {t.category && <span className={isMobile ? "text-[10px]" : "text-[9px]"} style={{ color: CATEGORY_COLORS[t.category] }}>{t.category}</span>}
      </div>
      {isMobile
        ? <Plus className="h-4 w-4 text-primary shrink-0" />
        : <Plus className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />}
    </div>
  );

  // Wrap in a padded shell so items visually breathe like the previous space-y-2 list.
  return (
    <div style={style} className="px-2">
      <div className={isMobile ? "py-1" : "py-1"}>
        {isMobile ? (
          <button data-testid={testid} onClick={() => onClick(t)} className="w-full text-left" style={{ height: MOBILE_ROW_HEIGHT - 8 }}>
            {inner}
          </button>
        ) : (
          <div data-testid={testid} draggable onDragStart={(e) => onDragStart(e, t)} onClick={() => onClick(t)} style={{ height: DESKTOP_ROW_HEIGHT - 8 }}>
            {inner}
          </div>
        )}
      </div>
    </div>
  );
});

export function TechniquePalette({
  techniques,
  q,
  setQ,
  catFilter,
  setCatFilter,
  onSelect,
  onDragStart,
  variant = "desktop",
  searchTestid,
  filterTestid,
  autoFocusSearch = false,
}) {
  const filtered = useMemo(
    () => techniques.filter((t) => !catFilter || t.category === catFilter),
    [techniques, catFilter],
  );
  const isMobile = variant === "mobile";
  const rowHeight = isMobile ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT;

  const handleSelect = useCallback((t) => onSelect?.(t), [onSelect]);
  const handleDragStart = useCallback((e, t) => onDragStart?.(e, t), [onDragStart]);

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 w-full overflow-hidden">
      <div className={`${isMobile ? "" : "px-3 pb-3 border-b border-border"} space-y-2 min-w-0`}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid={searchTestid}
            data-vaul-no-drag="true"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search techniques"
            autoFocus={autoFocusSearch}
            inputMode="search"
            enterKeyHint="search"
            className={`pl-8 ${isMobile ? "h-10" : "h-9"} bg-background border-border text-base md:text-sm`}
          />
        </div>
        <div className="flex flex-wrap gap-1" data-testid={filterTestid}>
          <button
            type="button"
            onClick={() => setCatFilter("")}
            data-testid={isMobile ? "mobile-cat-chip-all" : "cat-chip-all"}
            className={`text-[10px] uppercase tracking-wider px-2 rounded-full border transition-colors ${isMobile ? "py-1.5" : "py-1"} ${!catFilter ? "bg-white/10 border-white/20 text-white" : "border-border text-muted-foreground hover:text-white"}`}
          >
            All
          </button>
          {CATEGORIES.map((c) => {
            const active = catFilter === c;
            const color = CATEGORY_COLORS[c];
            const slug = c.toLowerCase().replace(/\s+/g, "-");
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCatFilter(active ? "" : c)}
                data-testid={isMobile ? `mobile-cat-chip-${slug}` : `cat-chip-${slug}`}
                className={`text-[10px] uppercase tracking-wider px-2 rounded-full border transition-colors ${isMobile ? "py-1.5" : "py-1"}`}
                style={
                  active
                    ? { background: `${color}25`, borderColor: `${color}80`, color }
                    : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }
                }
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 min-h-0 min-w-0 relative overscroll-contain" data-testid={isMobile ? "mobile-palette-list" : "palette-list"}>
        <div className="absolute inset-0">
          {techniques.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4 text-center">No techniques. Add some in the Library first.</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4 text-center">No {catFilter.toLowerCase()} techniques match.</p>
          ) : (
            <List
              className="pt-1 pb-2"
              rowComponent={PaletteRow}
              rowCount={filtered.length}
              rowHeight={rowHeight}
              rowProps={{
                techniques: filtered,
                variant,
                onDragStart: handleDragStart,
                onClick: handleSelect,
              }}
              defaultHeight={480}
              overscanCount={4}
            />
          )}
        </div>
      </div>
    </div>
  );
}
